<template>
    <v-dialog
        v-model="open"
        max-width="1180"
        transition="dialog-transition"
    >
        <v-card class="document-preview-dialog">
            <v-toolbar dark color="success" class="document-preview-toolbar">
                <div v-if="hasContext" class="document-preview-context">
                    <div v-if="accountLine" class="document-preview-context-main">
                        {{ accountLine }}
                    </div>
                    <div v-if="subject" class="document-preview-context-meta">
                        {{ subject }}
                    </div>
                    <div v-if="receivedLabel" class="document-preview-context-meta">
                        {{ receivedLabel }}
                    </div>
                </div>
                <v-toolbar-title class="white--text document-preview-title">
                    {{ name || "Document" }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <div v-if="isImage" class="document-preview-zoom-controls">
                    <v-btn
                        small
                        dark
                        color="default"
                        class="document-preview-zoom-btn"
                        title="Zoom out"
                        :disabled="scale <= minScale"
                        @click="zoomOut"
                    >
                        <v-icon small>mdi-minus</v-icon>
                    </v-btn>
                    <span class="document-preview-zoom-label">{{ zoomPercent }}%</span>
                    <v-btn
                        small
                        dark
                        color="default"
                        class="document-preview-zoom-btn"
                        title="Zoom in"
                        :disabled="scale >= maxScale"
                        @click="zoomIn"
                    >
                        <v-icon small>mdi-plus</v-icon>
                    </v-btn>
                    <v-btn
                        small
                        dark
                        color="default"
                        class="ml-1"
                        @click="fitToView"
                    >
                        Fit
                    </v-btn>
                </div>
                <v-btn
                    v-if="url"
                    small
                    class="document-preview-action mr-2"
                    :href="url"
                    target="_blank"
                    rel="noopener"
                >
                    <v-icon small class="mr-1">mdi-open-in-new</v-icon>
                    Open in new tab
                </v-btn>
                <v-btn
                    small
                    class="document-preview-action"
                    @click="close"
                >
                    <v-icon small class="mr-1">mdi-close</v-icon>
                    Close
                </v-btn>
            </v-toolbar>
            <div
                v-if="isImage"
                ref="viewport"
                class="document-preview-viewport"
                :class="{ 'document-preview-viewport--panning': panning }"
                @wheel="onWheel"
                @mousedown="onPanStart"
                @dblclick="onDoubleClick"
            >
                <img
                    v-if="url"
                    ref="previewImage"
                    class="document-preview-image"
                    :class="{ 'document-preview-image--ready': imageReady }"
                    :src="url"
                    :alt="name"
                    :style="imageStyle"
                    draggable="false"
                    @load="scheduleFit"
                />
            </div>
            <div
                v-else-if="kind === 'pdf' || kind === 'other'"
                class="document-preview-frame-wrap"
            >
                <iframe
                    v-if="frameSrc"
                    :key="frameSrc"
                    class="document-preview-frame"
                    :src="frameSrc"
                ></iframe>
            </div>
            <div v-else-if="kind === 'audio'" class="document-preview-audio-wrap">
                <v-icon size="64" color="#90a4ae">mdi-file-music</v-icon>
                <audio class="mt-4" controls :src="url"></audio>
            </div>
            <video
                v-else-if="kind === 'video'"
                class="document-preview-video"
                controls
                :src="url"
            ></video>
        </v-card>
    </v-dialog>
</template>
<script>
import moment from "moment";

export default {
    name: "DocumentPreviewDialog",
    props: {
        value: Boolean,
        url: String,
        kind: String,
        name: String,
        accountName: String,
        accountID: String,
        subject: String,
        received: [String, Date, Number],
    },
    data() {
        return {
            scale: 1,
            fitScale: 1,
            translateX: 0,
            translateY: 0,
            panning: false,
            panLastX: 0,
            panLastY: 0,
            minScale: 0.1,
            maxScale: 8,
            zoomStep: 0.2,
            imageReady: false,
            fitTimer: null,
            frameSrc: "",
            frameTimer: null,
        };
    },
    computed: {
        open: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit("input", value);
            },
        },
        isImage() {
            return this.kind === "image";
        },
        hasContext() {
            return !!(this.accountLine || this.subject || this.receivedLabel);
        },
        accountLine() {
            var name = (this.accountName || "").trim();
            var id = (this.accountID || "").trim();
            if (name && id) {
                return name + " · " + id;
            }
            return name || id;
        },
        receivedLabel() {
            if (!this.received) {
                return "";
            }
            var parsed = moment(this.received);
            if (!parsed.isValid()) {
                return "";
            }
            return parsed.format("MM/DD/YYYY h:mm A");
        },
        zoomPercent() {
            return Math.round(this.scale * 100);
        },
        imageStyle() {
            return {
                transform:
                    "translate(" +
                    this.translateX +
                    "px, " +
                    this.translateY +
                    "px) scale(" +
                    this.scale +
                    ")",
            };
        },
        previewUrl() {
            if (!this.url) {
                return "";
            }

            if (this.kind === "pdf" && this.url.indexOf("#") === -1) {
                return this.url + "#navpanes=0&view=FitH";
            }

            return this.url;
        },
    },
    watch: {
        value: function(isOpen) {
            if (isOpen) {
                this.bindPanListeners();
                this.scheduleFit();
                this.scheduleFrame();
            } else {
                this.unbindPanListeners();
                this.panning = false;
                this.clearFitTimer();
                this.clearFrameTimer();
                this.frameSrc = "";
            }
        },
        url: function() {
            this.imageReady = false;
            this.scale = 1;
            this.translateX = 0;
            this.translateY = 0;
            this.scheduleFit();
            this.scheduleFrame();
        },
    },
    beforeDestroy() {
        this.clearFitTimer();
        this.clearFrameTimer();
        this.unbindPanListeners();
    },
    methods: {
        close() {
            this.open = false;
        },
        clearFitTimer() {
            if (this.fitTimer) {
                clearTimeout(this.fitTimer);
                this.fitTimer = null;
            }
        },
        scheduleFit() {
            this.clearFitTimer();
            var self = this;
            var attempts = 0;
            function tryFit() {
                if (self.fitToView()) {
                    return;
                }
                attempts += 1;
                if (attempts < 30) {
                    self.fitTimer = setTimeout(tryFit, 50);
                }
            }
            this.$nextTick(tryFit);
        },
        clearFrameTimer() {
            if (this.frameTimer) {
                clearTimeout(this.frameTimer);
                this.frameTimer = null;
            }
        },
        scheduleFrame() {
            this.clearFrameTimer();
            this.frameSrc = "";
            if (!this.open || (this.kind !== "pdf" && this.kind !== "other") || !this.previewUrl) {
                return;
            }

            var self = this;
            this.$nextTick(function() {
                self.frameTimer = setTimeout(function() {
                    self.frameSrc = self.previewUrl;
                }, 80);
            });
        },
        bindPanListeners() {
            if (this._panMove || this._panEnd) {
                return;
            }
            this._panMove = this.onPanMove.bind(this);
            this._panEnd = this.onPanEnd.bind(this);
            window.addEventListener("mousemove", this._panMove);
            window.addEventListener("mouseup", this._panEnd);
        },
        unbindPanListeners() {
            if (this._panMove) {
                window.removeEventListener("mousemove", this._panMove);
                this._panMove = null;
            }
            if (this._panEnd) {
                window.removeEventListener("mouseup", this._panEnd);
                this._panEnd = null;
            }
        },
        fitToView() {
            var viewport = this.$refs.viewport;
            var image = this.$refs.previewImage;
            if (!viewport || !image || !image.naturalWidth || !image.naturalHeight) {
                return false;
            }

            var pad = 24;
            var viewW = viewport.clientWidth - pad;
            var viewH = viewport.clientHeight - pad;
            if (viewW <= 40 || viewH <= 40) {
                return false;
            }

            var nextScale = Math.min(
                viewW / image.naturalWidth,
                viewH / image.naturalHeight,
            );
            if (!nextScale || nextScale <= 0) {
                return false;
            }

            this.fitScale = nextScale;
            this.minScale = Math.max(0.1, nextScale * 0.5);
            this.scale = nextScale;
            this.translateX = (viewport.clientWidth - image.naturalWidth * nextScale) / 2;
            this.translateY = (viewport.clientHeight - image.naturalHeight * nextScale) / 2;
            this.imageReady = true;
            return true;
        },
        zoomBy(delta, clientX, clientY) {
            var next = this.scale * (delta > 0 ? 1 + this.zoomStep : 1 / (1 + this.zoomStep));
            this.zoomTo(next, clientX, clientY);
        },
        zoomIn() {
            this.zoomTowardCenter(this.scale * (1 + this.zoomStep));
        },
        zoomOut() {
            this.zoomTowardCenter(this.scale / (1 + this.zoomStep));
        },
        zoomTowardCenter(nextScale) {
            var viewport = this.$refs.viewport;
            if (!viewport) {
                this.scale = this.clampScale(nextScale);
                return;
            }

            var rect = viewport.getBoundingClientRect();
            this.zoomTo(nextScale, rect.left + rect.width / 2, rect.top + rect.height / 2);
        },
        zoomTo(nextScale, clientX, clientY) {
            var viewport = this.$refs.viewport;
            nextScale = this.clampScale(nextScale);
            if (!viewport || nextScale === this.scale) {
                this.scale = nextScale;
                return;
            }

            var rect = viewport.getBoundingClientRect();
            var mx = clientX - rect.left;
            var my = clientY - rect.top;
            var ratio = nextScale / this.scale;
            this.translateX = mx - (mx - this.translateX) * ratio;
            this.translateY = my - (my - this.translateY) * ratio;
            this.scale = nextScale;
        },
        clampScale(value) {
            if (value < this.minScale) {
                return this.minScale;
            }
            if (value > this.maxScale) {
                return this.maxScale;
            }
            return value;
        },
        onWheel(event) {
            event.preventDefault();
            var delta = event.deltaY < 0 ? 1 : -1;
            this.zoomBy(delta, event.clientX, event.clientY);
        },
        onDoubleClick(event) {
            if (this.scale > this.fitScale * 1.05) {
                this.fitToView();
                return;
            }

            this.zoomTo(this.scale * 2, event.clientX, event.clientY);
        },
        onPanStart(event) {
            if (event.button !== 0) {
                return;
            }

            event.preventDefault();
            this.panning = true;
            this.panLastX = event.clientX;
            this.panLastY = event.clientY;
        },
        onPanMove(event) {
            if (!this.panning) {
                return;
            }

            this.translateX += event.clientX - this.panLastX;
            this.translateY += event.clientY - this.panLastY;
            this.panLastX = event.clientX;
            this.panLastY = event.clientY;
        },
        onPanEnd() {
            this.panning = false;
        },
    },
};
</script>
<style>
.document-preview-dialog {
    display: flex;
    flex-direction: column;
    height: 88vh;
    max-height: 960px;
    overflow: hidden;
}

.document-preview-toolbar {
    flex: 0 0 auto;
}

.document-preview-toolbar.v-toolbar:not(.v-toolbar--fixed) .v-toolbar__content,
.document-preview-toolbar .v-toolbar__content,
.document-preview-toolbar .toolbar__content {
    margin-left: 0 !important;
    padding-left: 16px;
}

.document-preview-title,
.document-preview-toolbar .v-toolbar__title,
.document-preview-toolbar .toolbar__title {
    margin-left: 16px;
    padding-left: 0;
}

.document-preview-context {
    flex: 0 1 auto;
    min-width: 0;
    max-width: 360px;
    margin-left: 0;
    margin-right: 8px;
    line-height: 1.25;
}

.document-preview-context-main {
    font-size: 13px;
    font-weight: 600;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.document-preview-context-meta {
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.88);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.document-preview-action {
    border-radius: 18px !important;
    font-weight: 600;
    letter-spacing: 0.2px;
    text-transform: none !important;
    height: 32px !important;
    background-color: #fff !important;
    color: #2e7d32 !important;
    box-shadow: none !important;
}

.document-preview-action .v-icon,
.document-preview-action .icon {
    color: #2e7d32 !important;
}

.document-preview-action:hover {
    background-color: #e8f5e9 !important;
}

.document-preview-zoom-controls {
    display: flex;
    align-items: center;
    margin-right: 12px;
}

.document-preview-zoom-btn {
    min-width: 32px !important;
    width: 32px;
    padding: 0 !important;
}

.document-preview-zoom-label {
    min-width: 52px;
    text-align: center;
    font-size: 13px;
    font-weight: 600;
    color: #fff;
}

.document-preview-viewport {
    flex: 1 1 auto;
    height: calc(88vh - 64px);
    min-height: 420px;
    background: #37474f;
    overflow: hidden;
    cursor: grab;
    position: relative;
}

.document-preview-viewport--panning {
    cursor: grabbing;
}

.document-preview-image {
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    max-width: none;
    transform-origin: 0 0;
    user-select: none;
    pointer-events: none;
    visibility: hidden;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
    background: #fff;
}

.document-preview-image--ready {
    visibility: visible;
}

.document-preview-frame-wrap {
    flex: 1 1 auto;
    height: calc(88vh - 64px);
    min-height: 420px;
    padding: 12px;
    background: #37474f;
    box-sizing: border-box;
}

.document-preview-frame {
    display: block;
    width: 100%;
    height: 100%;
    min-height: 420px;
    border: 0;
    border-radius: 6px;
    background: #fff;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
}

.document-preview-video {
    display: block;
    max-width: calc(100% - 24px);
    max-height: calc(88vh - 96px);
    margin: 12px auto;
    border-radius: 6px;
    box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
}

.document-preview-audio-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1 1 auto;
    background: #37474f;
    color: #fff;
}
</style>
